/**
 * The fixture world.
 *
 * This is not decoration. Balances are produced by replaying a ledger — opening
 * credit, top-ups, pull-backs and every settled bet, in chronological order —
 * so a member's statement genuinely sums to their balance, exposure genuinely
 * comes from the open book, and a hierarchy P&L report genuinely reconciles with
 * the member rows beneath it. Charts drawn on this data are charts of something.
 *
 * Swapping to live APIs replaces `src/data/api.ts` only; nothing outside this
 * folder imports from here.
 */

import { createRng, quantise, type Rng } from './rng'
import {
  DEVICES,
  DISPLAY_FIRST,
  DISPLAY_LAST,
  EVENT_TEMPLATES,
  TRANSFER_REMARKS_GIVE,
  TRANSFER_REMARKS_TAKE,
  USERNAME_STEMS,
  type EventTemplate,
} from './catalog'
import {
  ROLES,
  type AccountFlag,
  type AccountRecord,
  type ActivityEvent,
  type Bet,
  type DailyPoint,
  type GameBucket,
  type MarketType,
  type PnlRow,
  type Role,
  type Session,
  type Sport,
  type Transfer,
} from './types'
import { bookBySelection, legOf, round2, splitByShare, tick } from '@/lib/book'
import { isoDay } from '@/lib/format'

/* ------------------------------------------------------------------ shape -- */

export interface MarketInstance {
  id: string
  eventId: string
  name: string
  type: MarketType
  selections: string[]
  /** True probabilities used to price the market and to settle it. */
  probs: number[]
  winner: string | null
}

export interface EventInstance {
  id: string
  name: string
  sport: Sport
  competition: string
  startsAt: string
  /** open = accepting bets, live = in play, settled = resulted. */
  state: 'open' | 'live' | 'settled'
  markets: MarketInstance[]
}

export interface World {
  now: number
  session: Session
  /**
   * STORED rows. Deliberately not `Account`: whether an account is stopped is
   * derived from the suspension records in `api.ts` on every read, so the world
   * cannot hand one out ready-made and have it silently go stale the moment an
   * ancestor is suspended.
   */
  accounts: AccountRecord[]
  byId: Map<string, AccountRecord>
  children: Map<string, string[]>
  /** Every account id at or beneath a given id, including itself. */
  subtree: Map<string, string[]>
  events: EventInstance[]
  eventById: Map<string, EventInstance>
  bets: Bet[]
  pnl: PnlRow[]
  transfers: Transfer[]
  activity: ActivityEvent[]
  daily: DailyPoint[]
}

/* --------------------------------------------------------------- helpers -- */

const DAY = 86_400_000

const startOfDay = (t: number) => {
  const d = new Date(t)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/** Commission the book charges on a member's net winnings, by market type. */
function commissionRate(type: MarketType): number {
  switch (type) {
    case 'Fancy':
    case 'Premium Fancy':
      return 0.03
    case 'Bookmaker':
      return 0.02
    case 'Live Casino':
      return 0
    default:
      return 0.02
  }
}

/** The reporting bucket a market type rolls up into. Exported so `api.ts` can
 *  apply a game filter to BETS, which carry a market type but no bucket. */
export function gameBucket(type: MarketType): GameBucket {
  switch (type) {
    case 'Bookmaker':
      return 'SportsBook - Bookmaker'
    case 'Premium Fancy':
      return 'Premium Fancy'
    case 'Fancy':
      return 'Fancy'
    case 'Live Casino':
      return 'Live Casino'
    default:
      return 'SportsBook'
  }
}

/** Default share in percentage points by role — totals 100 down a full chain. */
const BASE_SHARE: Record<Role, number> = {
  'company-master': 8,
  'triple-master': 10,
  'double-master': 14,
  'super-master': 20,
  master: 48,
  player: 0,
}

/* --------------------------------------------------------------- builders -- */

interface Draft
  extends Omit<
    AccountRecord,
    'balance' | 'exposure' | 'settlement' | 'flags' | 'childCount' | 'memberCount'
  > {
  /** Betting appetite 0..1 — drives stake size and frequency. */
  appetite: number
  /** Skill 0..1 — nudges win rate so some members genuinely run hot. */
  edge: number
}

function buildHierarchy(rng: Rng, now: number): { drafts: Draft[]; sessionId: string } {
  const drafts: Draft[] = []
  const usedNames = new Set<string>()

  const username = (stem: string, suffix: string) => {
    let name = `${stem}${suffix}`
    let n = 1
    while (usedNames.has(name)) name = `${stem}${suffix}${++n}`
    usedNames.add(name)
    return name
  }

  const displayName = () => `${rng.pick(DISPLAY_FIRST)} ${rng.pick(DISPLAY_LAST)}`

  const make = (
    id: string,
    name: string,
    role: Role,
    parent: Draft | null,
    createdAt: number,
    creditLimit: number,
  ): Draft => {
    const path = parent ? [...parent.path, parent.id] : []
    const activeRecently = rng.chance(role === 'player' ? 0.72 : 0.9)
    const draft: Draft = {
      id,
      username: name,
      displayName: role === 'player' ? displayName() : `${displayName()} (${name})`,
      role,
      parentId: parent?.id ?? null,
      path,
      status: rng.chance(0.94) ? 'active' : rng.chance(0.6) ? 'inactive' : 'suspended',
      createdAt: new Date(createdAt).toISOString(),
      lastActiveAt: activeRecently
        ? new Date(now - rng.int(0, 6) * DAY - rng.int(0, DAY)).toISOString()
        : rng.chance(0.8)
          ? new Date(now - rng.int(14, 90) * DAY).toISOString()
          : null,
      creditLimit,
      share: {
        sportsbook: round2(BASE_SHARE[role] * rng.float(0.85, 1.15)),
        casino: round2(BASE_SHARE[role] * rng.float(0.8, 1.1)),
      },
      commission: {
        fancy: round2(rng.float(0.5, 1)),
        bookmaker: round2(rng.float(0.05, 0.1)),
        matchOdds: round2(rng.float(0.02, 0.06)),
      },
      betLimits: {
        min: role === 'player' ? rng.pick([100, 100, 200, 500]) : 100,
        max: role === 'player' ? rng.pick([25_000, 50_000, 100_000, 200_000]) : 0,
      },
      games: {
        casino: rng.chance(0.9),
        cardGame: rng.chance(0.85),
        liveGame: rng.chance(0.8),
        sportsbook: true,
        premiumSportsbook: rng.chance(0.7),
      },
      appetite: rng.float(0.05, 1),
      edge: rng.gauss(0.5, 0.14, 0.1, 0.9),
    }
    drafts.push(draft)
    return draft
  }

  const t0 = now - 400 * DAY

  const root = make('acc-cm-1', 'w7ops', 'company-master', null, t0, 50_000_000)
  const tm = make('acc-tm-1', 'zintm1', 'triple-master', root, t0 + 5 * DAY, 12_000_000)
  const session = make('acc-dm-1', 'zinrtest1', 'double-master', tm, t0 + 12 * DAY, 2_500_000)
  session.displayName = 'Zin Retail Desk (zinrtest1)'
  session.status = 'active'

  // A sibling under the same triple master — proves subtree scoping hides it.
  const sibling = make('acc-dm-2', 'zinrtest2', 'double-master', tm, t0 + 20 * DAY, 1_800_000)
  for (let i = 0; i < 3; i++) {
    make(`acc-out-${i}`, username(rng.pick(USERNAME_STEMS), 'out'), 'player', sibling, t0 + 40 * DAY, 5_000)
  }

  let seq = 0
  const superMasters: Draft[] = []
  for (let s = 0; s < 4; s++) {
    const sm = make(
      `acc-sm-${s + 1}`,
      username(rng.pick(USERNAME_STEMS), 'sm'),
      'super-master',
      session,
      t0 + rng.int(20, 90) * DAY,
      rng.pick([300_000, 400_000, 500_000, 750_000]),
    )
    superMasters.push(sm)

    const masterCount = rng.int(2, 4)
    for (let m = 0; m < masterCount; m++) {
      const master = make(
        `acc-m-${++seq}`,
        username(rng.pick(USERNAME_STEMS), 'mn'),
        'master',
        sm,
        t0 + rng.int(60, 160) * DAY,
        rng.pick([60_000, 100_000, 150_000, 250_000]),
      )

      const playerCount = rng.int(5, 11)
      for (let p = 0; p < playerCount; p++) {
        make(
          `acc-p-${drafts.length}`,
          username(rng.pick(USERNAME_STEMS), rng.pick(['inr', 'pl', 'demo', '', 'usr'])),
          'player',
          master,
          t0 + rng.int(120, 330) * DAY,
          rng.pick([500, 1_000, 1_000, 2_000, 2_500, 5_000, 10_000]),
        )
      }
    }

    // A couple of players hung directly off the super master, as operators do.
    for (let p = 0; p < rng.int(0, 2); p++) {
      make(
        `acc-p-${drafts.length}`,
        username(rng.pick(USERNAME_STEMS), 'inr'),
        'player',
        sm,
        t0 + rng.int(150, 340) * DAY,
        rng.pick([2_000, 5_000, 10_000]),
      )
    }
  }

  // Players directly under the signed-in double master.
  for (let p = 0; p < 6; p++) {
    make(
      `acc-p-${drafts.length}`,
      username(rng.pick(USERNAME_STEMS), rng.pick(['inr', 'demo', 'pl'])),
      'player',
      session,
      t0 + rng.int(100, 340) * DAY,
      rng.pick([1_000, 2_500, 5_000, 10_000, 25_000]),
    )
  }

  return { drafts, sessionId: session.id }
}

function buildEvents(rng: Rng, now: number, days: number): EventInstance[] {
  const events: EventInstance[] = []
  const today = startOfDay(now)

  const priceMarket = (template: EventTemplate['markets'][number]): number[] => {
    const n = template.selections.length
    if (n === 2) {
      const p = rng.float(0.3, 0.7)
      return [p, 1 - p]
    }
    if (n === 3) {
      const a = rng.float(0.25, 0.5)
      const b = rng.float(0.2, 0.9 - a)
      return [a, b, Math.max(0.05, 1 - a - b)]
    }
    const raw = Array.from({ length: n }, () => rng.logNormal(1, 0.8))
    const sum = raw.reduce((s, v) => s + v, 0)
    return raw.map((v) => v / sum)
  }

  for (let d = days; d >= -1; d--) {
    const dayStart = today - d * DAY
    const templates = rng.sample(EVENT_TEMPLATES, rng.int(3, 6))
    templates.forEach((tpl, i) => {
      const startsAt = dayStart + rng.int(10, 21) * 3_600_000 + rng.int(0, 59) * 60_000
      const state: EventInstance['state'] =
        startsAt > now + 30 * 60_000 ? 'open' : startsAt > now - 3 * 3_600_000 ? 'live' : 'settled'
      const id = `evt-${d}-${i}`
      const markets: MarketInstance[] = tpl.markets.map((m, mi) => {
        const probs = priceMarket(m)
        let winner: string | null = null
        if (state === 'settled') {
          const roll = rng.next()
          let acc = 0
          winner = m.selections[m.selections.length - 1] ?? null
          for (let s = 0; s < m.selections.length; s++) {
            acc += probs[s] ?? 0
            if (roll <= acc) {
              winner = m.selections[s] ?? null
              break
            }
          }
        }
        return {
          id: `${id}-m${mi}`,
          eventId: id,
          name: m.name,
          type: m.type,
          selections: m.selections,
          probs,
          winner,
        }
      })
      events.push({
        id,
        name: tpl.name,
        sport: tpl.sport,
        competition: tpl.competition,
        startsAt: new Date(startsAt).toISOString(),
        state,
        markets,
      })
    })
  }

  return events
}

/* ------------------------------------------------------------------ build -- */

export interface BuildOptions {
  seed?: number | string
  now?: number
  /** Days of history to simulate. */
  days?: number
}

export function buildWorld(options: BuildOptions = {}): World {
  const { seed = 'w7-agent-console-v1', days = 180 } = options
  const now = options.now ?? Date.now()
  const rng = createRng(seed)

  const { drafts, sessionId } = buildHierarchy(rng.fork('hierarchy'), now)
  const draftById = new Map(drafts.map((d) => [d.id, d]))
  const events = buildEvents(rng.fork('events'), now, days)

  /* ---- relationships ---- */
  const children = new Map<string, string[]>()
  for (const d of drafts) {
    if (d.parentId) {
      const list = children.get(d.parentId) ?? []
      list.push(d.id)
      children.set(d.parentId, list)
    }
  }

  const subtree = new Map<string, string[]>()
  const collect = (id: string): string[] => {
    const cached = subtree.get(id)
    if (cached) return cached
    const out = [id]
    for (const child of children.get(id) ?? []) out.push(...collect(child))
    subtree.set(id, out)
    return out
  }
  for (const d of drafts) collect(d.id)

  /** Ancestors of a player, direct parent first, root last. */
  const uplineOf = (id: string) => {
    const d = draftById.get(id)
    if (!d) return []
    return d.path
      .slice()
      .reverse()
      .map((pid) => draftById.get(pid))
      .filter((x): x is Draft => Boolean(x))
  }

  /* ---- bets ---- */
  const players = drafts.filter((d) => d.role === 'player')
  const betRng = rng.fork('bets')
  const bets: Bet[] = []
  const pnl: PnlRow[] = []

  const weightedMarkets = (event: EventInstance) => {
    const tpl = EVENT_TEMPLATES.find((t) => t.name === event.name)
    return event.markets.map((m, i) => ({ market: m, weight: tpl?.markets[i]?.weight ?? 20 }))
  }

  const eventsByDay = new Map<string, EventInstance[]>()
  for (const e of events) {
    const key = isoDay(e.startsAt)
    const list = eventsByDay.get(key) ?? []
    list.push(e)
    eventsByDay.set(key, list)
  }

  let betSeq = 0
  for (const [, dayEvents] of eventsByDay) {
    const dayStart = startOfDay(new Date(dayEvents[0]!.startsAt).getTime())
    const dayIndex = Math.round((startOfDay(now) - dayStart) / DAY)
    // Weekends and the recent past are busier — gives the trend charts a pulse.
    const weekday = new Date(dayStart).getDay()
    const weekendLift = weekday === 0 || weekday === 6 ? 1.35 : 1
    const recencyLift = 1 + Math.max(0, (days - dayIndex) / days) * 0.5
    const count = Math.round(betRng.int(18, 42) * weekendLift * recencyLift)

    for (let i = 0; i < count; i++) {
      const player = betRng.pick(players)
      if (betRng.next() > 0.25 + player.appetite * 0.75) continue
      if (new Date(player.createdAt).getTime() > dayStart) continue
      if (player.status === 'closed') continue

      const event = betRng.pick(dayEvents)
      if (event.sport === 'Casino' && !player.games.casino) continue
      const pool = weightedMarkets(event)
      const totalWeight = pool.reduce((s, p) => s + p.weight, 0)
      let roll = betRng.float(0, totalWeight)
      let market = pool[0]!.market
      for (const p of pool) {
        roll -= p.weight
        if (roll <= 0) {
          market = p.market
          break
        }
      }
      if (market.type === 'Premium Fancy' && !player.games.premiumSportsbook) continue

      const selIndex = betRng.int(0, market.selections.length - 1)
      const selection = market.selections[selIndex]!
      const prob = market.probs[selIndex] ?? 0.5
      // Price around fair value with a little book margin and member-side noise.
      const priced = tick((1 / Math.max(0.02, prob)) * betRng.float(0.94, 1.06))
      const side: Bet['side'] = betRng.chance(market.selections.length > 3 ? 0.85 : 0.7) ? 'back' : 'lay'

      const stake = quantise(
        Math.min(
          player.betLimits.max || 50_000,
          Math.max(player.betLimits.min, player.appetite * player.creditLimit * betRng.logNormal(0.35, 0.7)),
        ),
        50,
      )

      const fullyMatched = betRng.chance(0.88)
      const matched = fullyMatched ? stake : quantise(stake * betRng.float(0.1, 0.85), 50)
      const unmatched = round2(stake - matched)
      const placedAt = new Date(
        Math.min(now - 60_000, dayStart + betRng.int(9, 22) * 3_600_000 + betRng.int(0, 3_500_000)),
      )
      // The recording shows 3–6 second place→match gaps on a busy market and
      // minutes on a quiet one. A spread over 90s reproduces that shape without
      // pretending to model matching; drawing from betRng keeps the world
      // byte-identical for a given seed.
      const matchedAt =
        matched > 0
          ? new Date(Math.min(now, placedAt.getTime() + betRng.int(1, 90) * 1000)).toISOString()
          : null

      const leg = { selection, side, matched, odds: priced }
      const liability = side === 'back' ? matched : round2(matched * (priced - 1))
      const potentialProfit = side === 'back' ? round2(matched * (priced - 1)) : matched

      let status: Bet['status']
      let result: Bet['result'] | undefined
      let memberPnl: number | undefined

      if (event.state === 'settled' && market.winner) {
        if (betRng.chance(0.012)) {
          status = 'void'
          result = 'void'
          memberPnl = 0
        } else if (betRng.chance(0.008)) {
          status = 'cancelled'
          memberPnl = 0
        } else {
          const won = market.winner === selection ? side === 'back' : side === 'lay'
          // A member's skill nudges outcomes slightly — some genuinely run hot.
          const skillFlip = betRng.next() < (player.edge - 0.5) * 0.12
          const finalWon = skillFlip ? !won : won
          status = 'settled'
          result = finalWon ? 'won' : 'lost'
          memberPnl = finalWon ? potentialProfit : -liability
        }
      } else {
        status = unmatched > 0 && matched === 0 ? 'unmatched' : 'matched'
      }

      const bet: Bet = {
        id: `bet-${++betSeq}`,
        memberId: player.id,
        placedAt: placedAt.toISOString(),
        matchedAt,
        sport: event.sport,
        eventId: event.id,
        eventName: event.name,
        marketId: market.id,
        marketName: market.name,
        marketType: market.type,
        selection,
        side,
        oddsPlaced: priced,
        oddsMatched: matched > 0 ? priced : null,
        stake,
        matched,
        unmatched,
        liability,
        potentialProfit,
        status,
        ...(result ? { result } : {}),
        ...(memberPnl !== undefined ? { pnl: round2(memberPnl) } : {}),
      }
      bets.push(bet)
      void leg

      if (bet.status === 'settled' && memberPnl !== undefined) {
        const commission = memberPnl > 0 ? round2(memberPnl * commissionRate(market.type)) : 0
        const netAmount = round2(memberPnl - commission)
        const chain = uplineOf(player.id).map((a) => ({ id: a.id, share: a.share.sportsbook }))
        const split = splitByShare(round2(-netAmount), chain)
        const settledAt = new Date(
          new Date(event.startsAt).getTime() + betRng.int(1, 4) * 3_600_000,
        ).toISOString()

        pnl.push({
          id: `pnl-${bet.id}`,
          settledAt,
          memberId: player.id,
          sport: event.sport,
          game: gameBucket(market.type),
          eventId: event.id,
          eventName: event.name,
          marketName: market.name,
          memberPnl: round2(memberPnl),
          commission,
          netAmount,
          split: Object.fromEntries(
            split.map((s) => [draftById.get(s.id)?.role ?? 'master', s.amount]),
          ) as PnlRow['split'],
          betCount: 1,
          turnover: matched,
        })
      }
    }
  }

  bets.sort((a, b) => a.placedAt.localeCompare(b.placedAt))
  pnl.sort((a, b) => a.settledAt.localeCompare(b.settledAt))

  /* ---- ledger replay: opening credit, settlements, top-ups ---- */
  const ledgerRng = rng.fork('ledger')
  const balances = new Map<string, number>(drafts.map((d) => [d.id, 0]))
  const transfers: Transfer[] = []
  let transferSeq = 0

  const move = (from: Draft, to: Draft, amount: number, at: number, remark: string) => {
    const amt = round2(amount)
    if (amt <= 0) return
    balances.set(from.id, round2((balances.get(from.id) ?? 0) - amt))
    balances.set(to.id, round2((balances.get(to.id) ?? 0) + amt))
    const giving = to.parentId === from.id
    transfers.push({
      id: `txn-${++transferSeq}`,
      ref: `W7${String(100000 + transferSeq)}`,
      at: new Date(at).toISOString(),
      fromId: from.id,
      toId: to.id,
      amount: amt,
      direction: giving ? 'give' : 'take',
      remark,
      balanceAfterFrom: balances.get(from.id) ?? 0,
      balanceAfterTo: balances.get(to.id) ?? 0,
      byId: giving ? from.id : to.id,
    })
  }

  // Seed the root so there is house money to hand down.
  balances.set('acc-cm-1', 80_000_000)

  type LedgerStep = { at: number; run: () => void }
  const steps: LedgerStep[] = []

  for (const d of drafts) {
    if (!d.parentId) continue
    const parent = draftById.get(d.parentId)
    if (!parent) continue
    const at = new Date(d.createdAt).getTime()
    const opening = quantise(d.creditLimit * ledgerRng.float(0.35, 0.9), 100)
    steps.push({ at, run: () => move(parent, d, opening, at, 'Opening credit') })
  }

  for (const row of pnl) {
    const at = new Date(row.settledAt).getTime()
    steps.push({
      at,
      run: () => {
        const member = draftById.get(row.memberId)
        if (!member) return
        balances.set(member.id, round2((balances.get(member.id) ?? 0) + row.netAmount))
        for (const [role, amount] of Object.entries(row.split)) {
          const ancestor = uplineOf(member.id).find((a) => a.role === role)
          if (ancestor && typeof amount === 'number') {
            balances.set(ancestor.id, round2((balances.get(ancestor.id) ?? 0) + amount))
          }
        }
      },
    })
  }

  // Periodic settlement sweeps between each account and its parent.
  for (const d of drafts) {
    if (!d.parentId) continue
    const parent = draftById.get(d.parentId)
    if (!parent) continue
    const created = new Date(d.createdAt).getTime()
    for (let at = created + 14 * DAY; at < now; at += ledgerRng.int(9, 26) * DAY) {
      const when = at
      steps.push({
        at: when,
        run: () => {
          const bal = balances.get(d.id) ?? 0
          if (bal < d.creditLimit * 0.15 && ledgerRng.chance(0.75)) {
            const amount = quantise(d.creditLimit * ledgerRng.float(0.2, 0.6), 100)
            move(parent, d, amount, when, ledgerRng.pick(TRANSFER_REMARKS_GIVE))
          } else if (bal > d.creditLimit * 0.9 && ledgerRng.chance(0.6)) {
            const amount = quantise((bal - d.creditLimit * 0.5) * ledgerRng.float(0.4, 0.9), 100)
            move(d, parent, amount, when, ledgerRng.pick(TRANSFER_REMARKS_TAKE))
          }
        },
      })
    }
  }

  steps.sort((a, b) => a.at - b.at)
  for (const step of steps) step.run()
  transfers.sort((a, b) => a.at.localeCompare(b.at))

  /* ---- exposure from the live book ---- */
  const openBets = bets.filter((b) => b.status === 'matched' || b.status === 'unmatched')
  const exposureByAccount = new Map<string, number>()

  const openByMarket = new Map<string, Bet[]>()
  for (const b of openBets) {
    const list = openByMarket.get(b.marketId) ?? []
    list.push(b)
    openByMarket.set(b.marketId, list)
  }

  const eventById = new Map(events.map((e) => [e.id, e]))
  const marketById = new Map<string, MarketInstance>()
  for (const e of events) for (const m of e.markets) marketById.set(m.id, m)

  for (const [marketId, marketBets] of openByMarket) {
    const market = marketById.get(marketId)
    if (!market) continue
    // Every account in the chain carries the same market's worst case, scoped to
    // the bets beneath it — which is exactly how a credit check reads it.
    const scopes = new Set<string>()
    for (const b of marketBets) {
      scopes.add(b.memberId)
      for (const a of uplineOf(b.memberId)) scopes.add(a.id)
    }
    for (const scopeId of scopes) {
      const ids = new Set(subtree.get(scopeId) ?? [scopeId])
      const legs = marketBets.filter((b) => ids.has(b.memberId)).map(legOf)
      if (legs.length === 0) continue
      const net = bookBySelection(legs, market.selections)
      const worstForMembers = Math.max(0, ...net.values())
      exposureByAccount.set(scopeId, round2((exposureByAccount.get(scopeId) ?? 0) + worstForMembers))
    }
  }

  /* ---- un-swept settlement position with the parent ---- */
  const lastSweep = new Map<string, number>()
  for (const t of transfers) {
    const at = new Date(t.at).getTime()
    lastSweep.set(t.fromId, Math.max(lastSweep.get(t.fromId) ?? 0, at))
    lastSweep.set(t.toId, Math.max(lastSweep.get(t.toId) ?? 0, at))
  }

  const settlementByAccount = new Map<string, number>()
  for (const row of pnl) {
    const at = new Date(row.settledAt).getTime()
    const member = draftById.get(row.memberId)
    if (!member) continue
    for (const id of [member.id, ...member.path]) {
      if (at <= (lastSweep.get(id) ?? 0)) continue
      settlementByAccount.set(id, round2((settlementByAccount.get(id) ?? 0) - row.netAmount))
    }
  }

  /* ---- finalise accounts ---- */
  const memberCounts = new Map<string, number>()
  for (const d of drafts) {
    memberCounts.set(d.id, (subtree.get(d.id) ?? []).filter((id) => draftById.get(id)?.role === 'player').length)
  }

  const accounts: AccountRecord[] = drafts.map((d) => {
    const balance = balances.get(d.id) ?? 0
    const exposure = exposureByAccount.get(d.id) ?? 0
    const settlement = settlementByAccount.get(d.id) ?? 0
    const lastActive = d.lastActiveAt ? new Date(d.lastActiveAt).getTime() : null
    const flags: AccountFlag[] = []

    const usage = d.creditLimit > 0 ? exposure / d.creditLimit : exposure > 0 ? 1 : 0
    if (usage > 1) flags.push('exposure-over-limit')
    else if (usage > 0.8) flags.push('exposure-near-limit')
    if (lastActive === null) flags.push('never-logged-in')
    else if (now - lastActive > 14 * DAY && balance > 500) flags.push('dormant-funded')
    if (Math.abs(settlement) > 1000) flags.push('settlement-overdue')

    const memberCount = d.role === 'player' ? 0 : (memberCounts.get(d.id) ?? 0)

    return {
      id: d.id,
      username: d.username,
      displayName: d.displayName,
      role: d.role,
      parentId: d.parentId,
      path: d.path,
      status: d.status,
      createdAt: d.createdAt,
      lastActiveAt: d.lastActiveAt,
      creditLimit: d.creditLimit,
      balance,
      exposure,
      settlement,
      share: d.share,
      commission: d.commission,
      betLimits: d.betLimits,
      games: d.games,
      childCount: (children.get(d.id) ?? []).length,
      memberCount,
      flags,
    }
  })

  const byId = new Map(accounts.map((a) => [a.id, a]))

  /* ---- activity ---- */
  const actRng = rng.fork('activity')
  const activity: ActivityEvent[] = []
  let actSeq = 0
  for (const t of transfers.slice(-400)) {
    activity.push({
      id: `act-${++actSeq}`,
      at: t.at,
      kind: 'transfer',
      actorId: t.byId,
      subjectId: t.byId === t.fromId ? t.toId : t.fromId,
      summary:
        t.direction === 'give'
          ? `Gave credit to ${byId.get(t.toId)?.username ?? t.toId}`
          : `Took credit from ${byId.get(t.fromId)?.username ?? t.fromId}`,
      detail: t.remark,
      amount: t.amount,
      ip: `49.36.${actRng.int(0, 255)}.${actRng.int(1, 254)}`,
      device: actRng.pick(DEVICES),
    })
  }
  for (const a of accounts) {
    activity.push({
      id: `act-${++actSeq}`,
      at: a.createdAt,
      kind: 'account-created',
      actorId: a.parentId ?? a.id,
      subjectId: a.id,
      summary: `Created ${a.username}`,
      detail: ROLES.includes(a.role) ? `Role: ${a.role}` : undefined,
      ip: `49.36.${actRng.int(0, 255)}.${actRng.int(1, 254)}`,
      device: actRng.pick(DEVICES),
    })
    if (a.lastActiveAt) {
      activity.push({
        id: `act-${++actSeq}`,
        at: a.lastActiveAt,
        kind: 'login',
        actorId: a.id,
        subjectId: a.id,
        summary: `${a.username} signed in`,
        ip: `49.36.${actRng.int(0, 255)}.${actRng.int(1, 254)}`,
        device: actRng.pick(DEVICES),
      })
    }
  }
  activity.sort((a, b) => b.at.localeCompare(a.at))

  /* ---- daily rollup for the whole subtree of the signed-in account ---- */
  const scope = new Set(subtree.get(sessionId) ?? [])
  const dayKeys: string[] = []
  for (let d = days; d >= 0; d--) dayKeys.push(isoDay(startOfDay(now) - d * DAY))

  const dailyMap = new Map<string, DailyPoint>(
    dayKeys.map((date) => [
      date,
      {
        date,
        turnover: 0,
        grossPnl: 0,
        commission: 0,
        netPnl: 0,
        bets: 0,
        activeMembers: 0,
        newMembers: 0,
        creditIn: 0,
        creditOut: 0,
      },
    ]),
  )
  const activeSets = new Map<string, Set<string>>(dayKeys.map((d) => [d, new Set<string>()]))

  for (const b of bets) {
    if (!scope.has(b.memberId)) continue
    const key = isoDay(b.placedAt)
    const point = dailyMap.get(key)
    if (!point) continue
    point.turnover = round2(point.turnover + b.matched)
    point.bets += 1
    activeSets.get(key)?.add(b.memberId)
  }
  for (const row of pnl) {
    if (!scope.has(row.memberId)) continue
    const key = isoDay(row.settledAt)
    const point = dailyMap.get(key)
    if (!point) continue
    // The book's result is the mirror of the member's.
    point.grossPnl = round2(point.grossPnl - row.memberPnl)
    point.commission = round2(point.commission + row.commission)
    point.netPnl = round2(point.netPnl - row.netAmount)
  }
  for (const t of transfers) {
    const key = isoDay(t.at)
    const point = dailyMap.get(key)
    if (!point) continue
    if (t.fromId === sessionId) point.creditOut = round2(point.creditOut + t.amount)
    if (t.toId === sessionId) point.creditIn = round2(point.creditIn + t.amount)
  }
  for (const a of accounts) {
    if (!scope.has(a.id)) continue
    const point = dailyMap.get(isoDay(a.createdAt))
    if (point) point.newMembers += 1
  }
  for (const [key, set] of activeSets) {
    const point = dailyMap.get(key)
    if (point) point.activeMembers = set.size
  }

  const sessionAccount = byId.get(sessionId)!

  return {
    now,
    session: {
      accountId: sessionId,
      username: sessionAccount.username,
      displayName: sessionAccount.displayName,
      role: sessionAccount.role,
      scopes: [],
    },
    accounts,
    byId,
    children,
    subtree,
    events,
    eventById,
    bets,
    pnl,
    transfers,
    activity,
    daily: dayKeys.map((d) => dailyMap.get(d)!),
  }
}

/** Built once per page load. */
let cached: World | null = null
export function getWorld(): World {
  cached ??= buildWorld()
  return cached
}

/** Test hook — forces a rebuild with explicit options. */
export function resetWorld(options?: BuildOptions): World {
  cached = buildWorld(options)
  return cached
}
